import { useEffect, useRef } from 'react';

export default function WebGLBackground({ canvasWidth = -1, canvasHeight = -1, opacity = 1, height = "60%", width = "100%", left= "0", zIndex = 1 }) {
  const canvasRef = useRef(null);
  const animationRef = useRef();
  const glRef = useRef(null);
  const programRef = useRef(null);
  const uniformsRef = useRef({ resolution: null, time: null });

  const vertexShaderSource = `#version 300 es
in vec4 aPosition;
void main() {
  gl_Position = aPosition;
}`;

  const fragmentShaderSource = `#version 300 es
precision highp float;

uniform vec3 iResolution;
uniform float iTime;
out vec4 fragColor;

/*
   "Plasma" by @XorDev
   Original Shadertoy version
*/

void main() {
    vec2 I = gl_FragCoord.xy - 0.5;  // pixel center fix
    vec2 r = iResolution.xy;

    // Centered, ratio corrected coordinates
    vec2 p = (I + I - r) / r.y;

    // Z depth (as vec2 to match original)
    vec2 z = vec2(0.0);
    // Iterator
    vec2 i = vec2(0.0);

    // Fluid coordinates
    vec2 f = p * (z += 4.0 - 4.0 * abs(0.7 - dot(p, p)));

    // Clear frag color
    vec4 O = vec4(0.0);

    // Loop 8 times (using i.y like original)
    for (O *= 0.0; i.y++ < 8.0;
         O += (sin(f) + 1.0).xyyx * abs(f.x - f.y)) {
        f += cos(f.yx * i.y + i + iTime) / i.y + 0.7;
    }

    // Tonemap, fade edges and gradient
    O = tanh(7.0 * exp(z.x - 4.0 - p.y * vec4(-1,1,2,0)) / O);

    fragColor = O;
}`;

  const createShader = (gl, type, source) => {
    const shader = gl.createShader(type);
    if (!shader) return null;

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  };

  const createProgram = (gl, vertexShader, fragmentShader) => {
    const program = gl.createProgram();
    if (!program) return null;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }
    return program;
  };

  const initWebGL = () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;

    const gl = canvas.getContext('webgl2');
    if (!gl) {
      console.warn('WebGL2 not supported, falling back to static background');
      return false;
    }

    glRef.current = gl;

    // Create shaders
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);

    if (!vertexShader || !fragmentShader) return false;

    // Create program
    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) return false;

    programRef.current = program;

    // Get uniform locations
    uniformsRef.current = {
      resolution: gl.getUniformLocation(program, 'iResolution'),
      time: gl.getUniformLocation(program, 'iTime'),
    };

    // Create position buffer
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    // Setup attributes
    const positionAttributeLocation = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(positionAttributeLocation);
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

    gl.useProgram(program);

    return true;
  };

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    const gl = glRef.current;
    if (!canvas || !gl) return;
    canvas.width = canvasWidth !=-1 ? canvasWidth : canvas.offsetWidth;
    canvas.height = canvasHeight !=-1 ? canvasHeight : canvas.offsetHeight;
    gl.viewport(0, 0, canvas.width, canvas.height);
  };

  const render = (time) => {
    const gl = glRef.current;
    const program = programRef.current;
    const uniforms = uniformsRef.current;

    if (!gl || !program) return;

    // Set uniforms
    if (uniforms.resolution) {
      gl.uniform3f(uniforms.resolution, gl.canvas.width, gl.canvas.height, 1.0);
    }
    if (uniforms.time) {
      gl.uniform1f(uniforms.time, time * 0.001);
    }

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    animationRef.current = requestAnimationFrame(render);
  };

  useEffect(() => {
    if (initWebGL()) {
      resizeCanvas();
      animationRef.current = requestAnimationFrame(render);

      const handleResize = () => resizeCanvas();

      window.addEventListener('resize', handleResize);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: left,
        width: width,
        height: height,
        opacity: opacity,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
    </div>
  );
}
